//
//  helpTourTargets.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines tour target identifiers and the shared tour step type.
//

import type { OnboardingTourStep } from "@gfazioli/mantine-onboarding-tour";
import type { BoxProps } from "@mantine/core";

import type { Permission } from "src/api/Tmd.dtos";
import { helpTourTargetAttribute } from "src/components/help/HelpTourTarget";

/**
 * Target identifiers rendered by the modern layout and the shared page shells. Page tours add
 * page-specific identifiers with {@link pageTarget}.
 */
export const helpTourTargets = {
    breadcrumbs: "layout-breadcrumbs",
    formActions: "form-actions",
    gridActions: "grid-actions",
    grid: "grid",
    gridFilter: "grid-filter",
    gridRow: "grid-row",
    help: "layout-help",
    navigation: "layout-navigation",
    pageActions: "page-actions",
    pageTitle: "page-title"
} as const;

/**
 * Turns visible text, such as a column header or menu label, into a stable id fragment.
 */
export const tourTargetSlug = (text: string) =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

/** The tour target of a grid column, marked on its header cell by the header text. */
export const columnTourTarget = (header: string) => `column-${tourTargetSlug(header)}`;

/** The tour target of a side menu item, marked by its label. */
export const menuTourTarget = (label: string) => `menu-${tourTargetSlug(label)}`;

/** The tour target of a navigation card, marked by its title. */
export const cardTourTarget = (title: string) => `card-${tourTargetSlug(title)}`;

/**
 * The tour target of a form field, marked on every grouped field by its property path. Fields in
 * repeated rows share a path relative to their row; the tour uses the first one displayed.
 */
export const fieldTourTarget = (propertyPath: string) => `field-${propertyPath}`;

/**
 * Props that mark a grouped field's existing root element as its tour target.
 */
export const fieldTourTargetProps = (propertyPath?: string | null) =>
    propertyPath ? tourTargetBoxProps(fieldTourTarget(propertyPath)) : tourTargetBoxProps();

/**
 * Props that mark a Mantine box, such as a grouped field's root, as the given tour target, or
 * mark nothing without an id.
 */
export const tourTargetBoxProps = (id?: string) =>
    // Mantine box props do not declare data attributes, though the root element renders them.
    (id ? { [helpTourTargetAttribute]: id } : {}) as Omit<BoxProps, "children">;

/**
 * Builds a page-specific target identifier, namespaced so it cannot collide with shared targets.
 */
export const pageTarget = (page: string, element: string) => `page-${page}-${element}`;

/** Stops inside the shared email recipient configuration panel. */
export const emailTourTargets = {
    search: pageTarget("emails", "search"),
    category: pageTarget("emails", "category"),
    email: pageTarget("emails", "email"),
    recipients: pageTarget("emails", "recipients"),
    from: pageTarget("emails", "from"),
    fallback: pageTarget("emails", "fallback"),
    edit: pageTarget("emails", "edit")
} as const;

/** Stops inside the shared collection data-entry form. */
export const entryTourTargets = {
    actions: pageTarget("entry", "actions"),
    section: pageTarget("entry", "section"),
    summary: pageTarget("entry", "summary"),
    na: pageTarget("entry", "na"),
    assignment: pageTarget("entry", "assignment"),
    question: pageTarget("entry", "question")
} as const;

/** Stops inside the template layout builder and the item component builder. */
export const builderTourTargets = {
    layout: pageTarget("builder", "layout"),
    primitives: pageTarget("builder", "primitives"),
    showInactive: pageTarget("builder", "show-inactive"),
    sectionTools: pageTarget("builder", "section-tools"),
    columnTools: pageTarget("builder", "column-tools"),
    itemTools: pageTarget("builder", "item-tools")
} as const;

/** The tour target of a builder insertion control, marked by its accessible label. */
export const insertionTourTarget = (label: string) => `insert-${tourTargetSlug(label)}`;

/** Stops on the bulk item update pages. */
export const bulkTourTargets = {
    template: pageTarget("bulk", "template"),
    entry: pageTarget("bulk", "entry"),
    actions: pageTarget("bulk", "actions")
} as const;

/** Stops on the template and item import pages. */
export const importTourTargets = {
    source: pageTarget("import", "source"),
    actions: pageTarget("import", "actions")
} as const;

/** Stops on the navigation card pages (settings, trial configuration, trial items, group configuration). */
export const navigationTourTargets = {
    cards: pageTarget("navigation", "cards")
} as const;

/** Stops on the HubSpot membership and personnel HubSpot pages. */
export const hubspotTourTargets = {
    summary: pageTarget("hubspot", "summary"),
    contact: pageTarget("hubspot", "contact")
} as const;

/** Stops on the trial capability and tag selection pages. */
export const selectionTourTargets = {
    actions: pageTarget("selection", "actions"),
    save: pageTarget("selection", "save"),
    category: pageTarget("selection", "category")
} as const;

/** Stops on the personnel email preference pages. */
export const preferenceTourTargets = {
    person: pageTarget("preferences", "person"),
    trial: pageTarget("preferences", "trial")
} as const;

/** Stops on the dashboard. */
export const dashboardTourTargets = {
    summaries: pageTarget("dashboard", "summaries")
} as const;

/** The tour target of one dashboard summary, marked on its heading by the heading text. */
export const dashboardSummaryTourTarget = (title: string) =>
    pageTarget("dashboard", tourTargetSlug(title));

/**
 * A tour step whose id names a HelpTourTarget. A step with a permission is only offered when the
 * user holds it at the page's layout context.
 */
export type HelpTourStep = OnboardingTourStep & {
    permission?: Permission;
    /** The part of the tour the step belongs to; steps without one appear in the whole tour only. */
    chapter?: string;
    /**
     * Identifies the step's stored title and text (see tourStepText). Unique within a tour; a step
     * without one uses its id.
     */
    key?: string;
};

/** Opening step shared by every tour, anchored to the page heading. */
export const pageTitleStep = (title: string, content: string): HelpTourStep => ({
    id: helpTourTargets.pageTitle,
    title,
    content
});

/** Closing step shared by every tour, anchored to the header help button. */
export const helpStep: HelpTourStep = {
    id: helpTourTargets.help,
    title: "Help is always here",
    content:
        "Open help at any time to read about the page you are on, or to take its tour again. Press Escape to leave a tour."
};
